import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Search } from "lucide-react";
import { Header, Footer, Loading } from "../components/Shell";
import { api } from "../lib/api";
import { useSeo } from "../lib/seo";

// Sprint 3 preview — Learning Hub with Level 0-4 structure.
// Renders full /learning-paths grouping. User can jump straight to a level via #level-N anchor.
export function Belajar() {
  const [paths, setPaths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  useSeo({
    title: "Belajar Digital Marketing — Learning Path Terstruktur",
    description:
      "Learning path terstruktur Level 0–4: Fundamental, Marketplace Ads, Social Ads, Advanced Ads, Scaling & Profit.",
    path: "/belajar",
  });
  useEffect(() => {
    api("/learning-paths")
      .then((d) => setPaths(d.levels || []))
      .catch(() => {})
      .finally(() => setLoading(false));
    // If hash present, scroll after render
    setTimeout(() => {
      const hash = window.location.hash;
      if (hash) document.querySelector(hash)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 500);
  }, []);

  const filterCourses = (courses) => {
    if (!q.trim()) return courses;
    const needle = q.toLowerCase();
    return courses.filter(
      (c) =>
        c.title.toLowerCase().includes(needle) ||
        (c.description || "").toLowerCase().includes(needle)
    );
  };

  if (loading) return <Loading />;

  return (
    <div>
      <Header />
      <main>
        <section className="section" style={{ padding: "60px 32px 40px", textAlign: "center" }}>
          <span className="section-kicker">LEARNING HUB</span>
          <h1 style={{ fontSize: "2.4rem", margin: "12px 0", lineHeight: 1.15 }}>
            Anda ingin belajar <span>apa?</span>
          </h1>
          <p style={{ color: "#475569", maxWidth: 620, margin: "0 auto 32px" }}>
            5 level pembelajaran, urut dari fundamental sampai scaling.
            Ikuti runtut, atau lompat langsung ke materi yang Anda butuhkan.
          </p>
          <div style={{
            maxWidth: 520, margin: "0 auto",
            position: "relative", display: "flex", alignItems: "center",
          }}>
            <Search size={18} style={{ position: "absolute", left: 16, color: "#94a3b8" }} />
            <input
              type="text"
              placeholder="Cari materi (mis. Shopee, GMV Max, Meta Ads, dll)"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="belajar-search"
              style={{
                width: "100%", padding: "14px 16px 14px 44px",
                border: "1px solid #e2e8f0", borderRadius: 10,
                fontSize: "0.95rem", outline: "none",
              }}
            />
          </div>
        </section>

        {/* Level Navigation Chips */}
        <div style={{
          display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap",
          padding: "0 32px 32px",
        }} data-testid="belajar-chips">
          {paths.map((lvl) => (
            <a
              key={lvl.level}
              href={`#level-${lvl.level}`}
              data-testid={`chip-lvl-${lvl.level}`}
              style={{
                padding: "8px 16px", borderRadius: 999,
                background: `${lvl.color}18`, color: lvl.color,
                fontSize: "0.85rem", fontWeight: 600, textDecoration: "none",
                border: `1px solid ${lvl.color}55`,
              }}
            >
              Level {lvl.level} · {lvl.total_courses}
            </a>
          ))}
        </div>

        {/* Levels Rendered */}
        {paths.map((lvl) => (
          <section
            key={lvl.level}
            id={`level-${lvl.level}`}
            data-testid={`belajar-lvl-${lvl.level}`}
            style={{ padding: "40px 32px", maxWidth: 1200, margin: "0 auto", scrollMarginTop: 80 }}
          >
            <div style={{
              display: "flex", alignItems: "center", gap: 16, marginBottom: 24,
              paddingBottom: 16, borderBottom: `2px solid ${lvl.color}33`,
            }}>
              <div style={{
                background: lvl.color, color: "#fff", borderRadius: 12,
                width: 56, height: 56, display: "flex", alignItems: "center",
                justifyContent: "center", fontSize: 24, fontWeight: 800,
              }}>{lvl.level}</div>
              <div>
                <h2 style={{ margin: 0, fontSize: "1.6rem" }}>
                  {lvl.title.replace(/^Level \d+ — /, "")}
                </h2>
                <small style={{ color: "#64748b" }}>{lvl.tagline}</small>
              </div>
            </div>

            {lvl.total_courses === 0 ? (
              <div style={{
                background: "#f8fafc", border: "2px dashed #cbd5e1",
                borderRadius: 12, padding: 40, textAlign: "center",
              }} data-testid={`empty-lvl-${lvl.level}`}>
                <BookOpen size={32} style={{ color: "#94a3b8", marginBottom: 12 }} />
                <b style={{ display: "block", marginBottom: 8, color: "#475569" }}>
                  Segera Hadir
                </b>
                <small style={{ color: "#64748b" }}>
                  Kursus untuk level ini sedang disiapkan.
                </small>
              </div>
            ) : (
              lvl.categories.map((cat) => {
                const filtered = filterCourses(cat.courses);
                if (q.trim() && filtered.length === 0) return null;
                return (
                  <div key={cat.slug} style={{ marginBottom: 32 }}>
                    <div style={{
                      display: "flex", alignItems: "center", gap: 12,
                      marginBottom: 16,
                    }}>
                      <span style={{ fontSize: 24 }}>{cat.icon}</span>
                      <div>
                        <b style={{ fontSize: "1.1rem" }}>{cat.title}</b>
                        {cat.description && (
                          <small style={{ display: "block", color: "#64748b" }}>
                            {cat.description}
                          </small>
                        )}
                      </div>
                    </div>
                    <div style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))",
                      gap: 16,
                    }}>
                      {filtered.map((c) => (
                        <Link
                          key={c.slug}
                          to={`/courses/${c.slug}`}
                          data-testid={`belajar-course-${c.slug}`}
                          style={{
                            background: "#fff", border: "1px solid #e2e8f0",
                            borderRadius: 12, padding: 20, textDecoration: "none",
                            color: "#0f172a", display: "flex", flexDirection: "column",
                            gap: 12, transition: "all 0.15s",
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.borderColor = lvl.color; e.currentTarget.style.transform = "translateY(-2px)"; }}
                          onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e2e8f0"; e.currentTarget.style.transform = "translateY(0)"; }}
                        >
                          <div style={{
                            display: "flex", gap: 6, alignItems: "center",
                            fontSize: "0.75rem", color: "#64748b",
                          }}>
                            <span style={{
                              background: `${lvl.color}18`, color: lvl.color,
                              padding: "2px 8px", borderRadius: 6, fontWeight: 600,
                            }}>Level {lvl.level}</span>
                            {c.level_label && <span>· {c.level_label}</span>}
                          </div>
                          <b style={{ fontSize: "1rem", lineHeight: 1.3 }}>{c.title}</b>
                          {c.description && (
                            <small style={{ color: "#64748b", lineHeight: 1.4 }}>
                              {c.description.slice(0, 100)}
                              {c.description.length > 100 ? "..." : ""}
                            </small>
                          )}
                          <div style={{
                            marginTop: "auto", display: "flex",
                            justifyContent: "space-between", alignItems: "center",
                            paddingTop: 12, borderTop: "1px solid #f1f5f9",
                          }}>
                            <b style={{ color: lvl.color }}>
                              Rp {Number(c.price).toLocaleString("id-ID")}
                            </b>
                            <span style={{
                              color: lvl.color, fontSize: "0.85rem",
                              display: "inline-flex", alignItems: "center", gap: 4,
                            }}>
                              Mulai <ArrowRight size={14} />
                            </span>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                );
              })
            )}
          </section>
        ))}

        {/* Final CTA */}
        <section style={{
          background: "#0f172a", color: "#f8fafc", padding: "60px 32px",
          textAlign: "center", margin: "40px 0 0",
        }} data-testid="belajar-cta">
          <h2 style={{ color: "#fff", fontSize: "1.8rem", marginBottom: 12 }}>
            Masih bingung mulai dari mana?
          </h2>
          <p style={{ color: "#cbd5e1", maxWidth: 520, margin: "0 auto 24px" }}>
            Chat langsung Admin untuk rekomendasi jalur belajar yang tepat sesuai kondisi bisnis Anda.
          </p>
          <a
            href="https://wa.me/6285157009099?text=Halo%20Admin%2C%20saya%20butuh%20rekomendasi%20jalur%20belajar%20di%20MASTOFA."
            target="_blank" rel="noreferrer"
            className="button"
            data-testid="belajar-cta-wa"
            style={{ background: "#fbbf24", color: "#0f172a", fontWeight: 700 }}
          >
            Konsultasi Gratis via WhatsApp <ArrowRight size={16} />
          </a>
        </section>
      </main>
      <Footer />
    </div>
  );
}

export default Belajar;
